import { Cost } from '@shiftly/shared/src/schema';
import { formatCurrencyGBP } from '../../../../utils/format';
import { UserPayData } from '../../../../lib/supabase/pay/supabasePayService';

export interface PaySummaryData {
  data: any[];
  columnCount: number;
}

export interface PaySummaryTotals {
  totalSales: number;
  ukSales: number;
  ukRegular: number;
  ukOT: number;
  fpSales: number;
  fpRegular: number;
  fpOT: number;
  bonus: number;
  scheduling: number;
  referrals: number;
  management: number;
  marketing: number;
  tools: number;
  commission: number;
  otherCosts: number;
  adDeductions: number;
}

export interface SetTotals {
  [setId: string]: PaySummaryTotals;
}

export const calculatePaySummaryTotals = (
  userPayData: UserPayData[],
  costs: Cost[]
): { overallTotals: PaySummaryTotals; setTotals: SetTotals; sortedSets: string[] } => {
  const setTotals: SetTotals = {};
  const overallTotals: PaySummaryTotals = {
    totalSales: 0,
    ukSales: 0,
    ukRegular: 0,
    ukOT: 0,
    fpSales: 0,
    fpRegular: 0,
    fpOT: 0,
    bonus: 0,
    scheduling: 0,
    referrals: 0,
    management: 0,
    marketing: 0,
    tools: 0,
    commission: 0,
    otherCosts: 0,
    adDeductions: 0
  };

  // Collect all sets
  const allSets = new Set<string>();

  userPayData.forEach(userData => {
    const breakdown = userData.breakdown;

    // Sales set totals
    if (breakdown.sales?.breakdown?.set_totals) {
      Object.keys(breakdown.sales.breakdown.set_totals).forEach(set => allSets.add(set));
    }

    // Other categories set totals
    ['management', 'marketing', 'tools', 'ad/ded', 'referrals'].forEach(category => {
      const categoryData = breakdown[category as keyof typeof breakdown] as any;
      if (categoryData?.set_totals) {
        Object.keys(categoryData.set_totals).forEach(set => allSets.add(set));
      }
    });

    // Scheduling has a different structure
    if (breakdown.scheduling?.breakdown?.set_totals) {
      Object.keys(breakdown.scheduling.breakdown.set_totals).forEach(set => allSets.add(set));
    }

    // Bonus has a different structure
    if (breakdown.bonus?.breakdown?.sales_bonus?.set_totals) {
      Object.keys(breakdown.bonus.breakdown.sales_bonus.set_totals).forEach(set => allSets.add(set));
    }
    if (breakdown.bonus?.breakdown?.misc_payment_bonus?.set_totals) {
      Object.keys(breakdown.bonus.breakdown.misc_payment_bonus.set_totals).forEach(set => allSets.add(set));
    }
  });

  costs.forEach(cost => allSets.add(cost.set.toString()));

  const sortedSets = Array.from(allSets).sort();

  // Initialize set totals
  sortedSets.forEach(set => {
    setTotals[set] = {
      totalSales: 0,
      ukSales: 0,
      ukRegular: 0,
      ukOT: 0,
      fpSales: 0,
      fpRegular: 0,
      fpOT: 0,
      bonus: 0,
      scheduling: 0,
      referrals: 0,
      management: 0,
      marketing: 0,
      tools: 0,
      commission: 0,
      otherCosts: 0,
      adDeductions: 0
    };
  });

  // Process user pay data
  userPayData.forEach(userData => {
    const breakdown = userData.breakdown;

    // Sales data
    if (breakdown.sales) {
      overallTotals.totalSales += breakdown.sales.total;

      if (breakdown.sales.breakdown?.set_totals) {
        Object.entries(breakdown.sales.breakdown.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            const totalPay = setData.total || 0;
            setTotals[set].totalSales += totalPay;

            // Distribute sales based on user's pay class
            if (userData.user_pay_class === 'UK') {
              setTotals[set].ukRegular += setData.regular_pay || 0;
              setTotals[set].ukOT += (setData.weekend_ot_pay || 0) + (setData.weekday_ot_pay || 0);
              setTotals[set].ukSales += totalPay;
              overallTotals.ukSales += totalPay;
              overallTotals.ukRegular += setData.regular_pay || 0;
              overallTotals.ukOT += (setData.weekend_ot_pay || 0) + (setData.weekday_ot_pay || 0);
            } else if (userData.user_pay_class === 'FP') {
              setTotals[set].fpRegular += setData.regular_pay || 0;
              setTotals[set].fpOT += (setData.weekend_ot_pay || 0) + (setData.weekday_ot_pay || 0);
              setTotals[set].fpSales += totalPay;
              overallTotals.fpSales += totalPay;
              overallTotals.fpRegular += setData.regular_pay || 0;
              overallTotals.fpOT += (setData.weekend_ot_pay || 0) + (setData.weekday_ot_pay || 0);
            }
          }
        });
      }
    }

    // Bonus data
    if (breakdown.bonus) {
      overallTotals.bonus += breakdown.bonus.total;

      // Process sales_bonus set totals
      if (breakdown.bonus.breakdown?.sales_bonus?.set_totals) {
        Object.entries(breakdown.bonus.breakdown.sales_bonus.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            setTotals[set].bonus += setData.total || 0;
          }
        });
      }

      // Process misc_payment_bonus set totals
      if (breakdown.bonus.breakdown?.misc_payment_bonus?.set_totals) {
        Object.entries(breakdown.bonus.breakdown.misc_payment_bonus.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            setTotals[set].bonus += setData.total || 0;
          }
        });
      }
    }

    // Scheduling data
    if (breakdown.scheduling) {
      overallTotals.scheduling += breakdown.scheduling.total;
      if (breakdown.scheduling.breakdown?.set_totals) {
        Object.entries(breakdown.scheduling.breakdown.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            setTotals[set].scheduling += setData.total || 0;
          }
        });
      }
    }

    // Management data
    if (breakdown.management) {
      overallTotals.management += breakdown.management.total;
      const mgmtData = breakdown.management as any;
      if (mgmtData.set_totals) {
        Object.entries(mgmtData.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            setTotals[set].management += setData.total || 0;
          }
        });
      }
    }

    // Marketing data
    if (breakdown.marketing) {
      overallTotals.marketing += breakdown.marketing.total;
      const marketingData = breakdown.marketing as any;
      if (marketingData.set_totals) {
        Object.entries(marketingData.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            setTotals[set].marketing += setData.total || 0;
          }
        });
      }
    }

    // Tools data
    if (breakdown.tools) {
      overallTotals.tools += breakdown.tools.total;
      const toolsData = breakdown.tools as any;
      if (toolsData.set_totals) {
        Object.entries(toolsData.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            setTotals[set].tools += setData.total || 0;
          }
        });
      }
    }

    // Referrals data
    if (breakdown.referrals) {
      overallTotals.referrals += breakdown.referrals.total;
      const referralsData = breakdown.referrals as any;
      if (referralsData.set_totals) {
        Object.entries(referralsData.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            setTotals[set].referrals += setData.total || 0;
          }
        });
      }
    }

    // Ad/Deductions data
    if (breakdown['ad/ded']) {
      overallTotals.adDeductions += breakdown['ad/ded'].total;
      const adDedData = breakdown['ad/ded'] as any;
      if (adDedData.set_totals) {
        Object.entries(adDedData.set_totals).forEach(([set, setData]: [string, any]) => {
          if (setTotals[set]) {
            setTotals[set].adDeductions += setData.total || 0;
          }
        });
      }
    }
  });

  // Process costs
  costs.forEach(cost => {
    const setKey = cost.set.toString();
    if (setTotals[setKey]) {
      switch (cost.category) {
        case 'Commission':
          setTotals[setKey].commission += cost.amount;
          overallTotals.commission += cost.amount;
          break;
        case 'Tools':
          setTotals[setKey].tools += cost.amount;
          overallTotals.tools += cost.amount;
          break;
        case 'Other':
          setTotals[setKey].otherCosts += cost.amount;
          overallTotals.otherCosts += cost.amount;
          break;
        case 'Management':
          setTotals[setKey].management += cost.amount;
          overallTotals.management += cost.amount;
          break;
        case 'Referral':
          setTotals[setKey].referrals += cost.amount;
          overallTotals.referrals += cost.amount;
          break;
      }
    }
  });

  return { overallTotals, setTotals, sortedSets };
};

const getSectionColumns = (sectionIndex: number) => {
  if (sectionIndex === 0) {
    return { label: 'A', amount: 'B' };
  } else {
    const baseIndex = 3 + ((sectionIndex - 1) * 3);
    return {
      label: String.fromCharCode(65 + baseIndex),
      amount: String.fromCharCode(65 + baseIndex + 1)
    };
  }
};

const createEmptyRow = (columnCount: number) => {
  const emptyRow: any = {};
  for (let i = 0; i < columnCount; i++) {
    emptyRow[String.fromCharCode(65 + i)] = '';
  }
  return emptyRow;
};

const addCostDetailsRows = (
  data: any[],
  costs: Cost[],
  category: string,
  sortedSets: string[]
) => {
  const categoryCosts = costs.filter(cost => cost.category === category);
  const groups = new Map<string, any[]>();
  const singleCosts: any[] = [];

  categoryCosts.forEach(cost => {
    if (cost.metadata?.split_id) {
      if (!groups.has(cost.metadata.split_id)) {
        groups.set(cost.metadata.split_id, []);
      }
      groups.get(cost.metadata.split_id)!.push(cost);
    } else {
      singleCosts.push(cost);
    }
  });

  // Add split costs
  groups.forEach((splitCosts, _) => {
    const costName = splitCosts[0].name;
    const totalSplitAmount = splitCosts.reduce((sum, c) => sum + c.amount, 0);

    const splitRow: any = { A: `  ${costName}`, B: formatCurrencyGBP(totalSplitAmount), C: '' };
    sortedSets.forEach((set, index) => {
      const cols = getSectionColumns(index + 1);
      const costForSet = splitCosts.find(c => c.set.toString() === set);
      splitRow[cols.label] = `  ${costName}`;
      splitRow[cols.amount] = formatCurrencyGBP(costForSet?.amount || 0);
      if (index < sortedSets.length - 1) {
        const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
        splitRow[gapCol] = '';
      }
    });
    data.push(splitRow);
  });

  // Add single costs
  singleCosts.forEach(cost => {
    const costRow: any = { A: `  ${cost.name}`, B: formatCurrencyGBP(cost.amount), C: '' };
    sortedSets.forEach((set, index) => {
      const cols = getSectionColumns(index + 1);
      const costForSet = cost.set.toString() === set ? cost.amount : 0;
      costRow[cols.label] = `  ${cost.name}`;
      costRow[cols.amount] = formatCurrencyGBP(costForSet);
      if (index < sortedSets.length - 1) {
        const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
        costRow[gapCol] = '';
      }
    });
    data.push(costRow);
  });
};

export const generatePaySummarySpreadsheetData = (
  userPayData: UserPayData[],
  costs: Cost[]
): PaySummaryData => {
  const { overallTotals, setTotals, sortedSets } = calculatePaySummaryTotals(userPayData, costs);

  const columnCount = 2 + 1 + (sortedSets.length * 3);
  const data: any[] = [];

  // 1. Headers row
  const headers: any = { A: 'TOTAL', B: '', C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    headers[cols.label] = `SET ${set}`;
    headers[cols.amount] = '';
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      headers[gapCol] = '';
    }
  });
  data.push(headers);

  // 2. Total sales row
  const totalSalesRow: any = { A: 'Total Sales', B: formatCurrencyGBP(overallTotals.totalSales), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    totalSalesRow[cols.label] = 'Total Sales';
    totalSalesRow[cols.amount] = formatCurrencyGBP(setTotals[set].totalSales);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      totalSalesRow[gapCol] = '';
    }
  });
  data.push(totalSalesRow);

  // 3. Breakdown header
  const breakdownRow: any = { A: 'Breakdown', B: '', C: '' };
  sortedSets.forEach((_, index) => {
    const cols = getSectionColumns(index + 1);
    breakdownRow[cols.label] = 'Breakdown';
    breakdownRow[cols.amount] = '';
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      breakdownRow[gapCol] = '';
    }
  });
  data.push(breakdownRow);

  // 4. UK row
  const ukRow: any = { A: 'UK', B: formatCurrencyGBP(overallTotals.ukRegular + overallTotals.ukOT), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    ukRow[cols.label] = 'UK';
    ukRow[cols.amount] = formatCurrencyGBP(setTotals[set].ukRegular + setTotals[set].ukOT);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      ukRow[gapCol] = '';
    }
  });
  data.push(ukRow);

  // 5. UK Sales Fee row
  const ukSalesFeeRow: any = { A: 'Sales Fee', B: formatCurrencyGBP(overallTotals.ukRegular), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    ukSalesFeeRow[cols.label] = 'Sales Fee';
    ukSalesFeeRow[cols.amount] = formatCurrencyGBP(setTotals[set].ukRegular);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      ukSalesFeeRow[gapCol] = '';
    }
  });
  data.push(ukSalesFeeRow);

  // 6. UK OT Fee row
  const ukOTFeeRow: any = { A: 'OT Fee', B: formatCurrencyGBP(overallTotals.ukOT), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    ukOTFeeRow[cols.label] = 'OT Fee';
    ukOTFeeRow[cols.amount] = formatCurrencyGBP(setTotals[set].ukOT);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      ukOTFeeRow[gapCol] = '';
    }
  });
  data.push(ukOTFeeRow);

  // 7. Filipino row
  const fpRow: any = { A: 'Filipino', B: formatCurrencyGBP(overallTotals.fpRegular + overallTotals.fpOT), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    fpRow[cols.label] = 'Filipino';
    fpRow[cols.amount] = formatCurrencyGBP(setTotals[set].fpRegular + setTotals[set].fpOT);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      fpRow[gapCol] = '';
    }
  });
  data.push(fpRow);

  // 8. FP Sales Fee row
  const fpSalesFeeRow: any = { A: 'Sales Fee', B: formatCurrencyGBP(overallTotals.fpRegular), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    fpSalesFeeRow[cols.label] = 'Sales Fee';
    fpSalesFeeRow[cols.amount] = formatCurrencyGBP(setTotals[set].fpRegular);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      fpSalesFeeRow[gapCol] = '';
    }
  });
  data.push(fpSalesFeeRow);

  // 9. FP OT Fee row
  const fpOTFeeRow: any = { A: 'OT Fee', B: formatCurrencyGBP(overallTotals.fpOT), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    fpOTFeeRow[cols.label] = 'OT Fee';
    fpOTFeeRow[cols.amount] = formatCurrencyGBP(setTotals[set].fpOT);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      fpOTFeeRow[gapCol] = '';
    }
  });
  data.push(fpOTFeeRow);

  // 10-11. Empty rows
  data.push(createEmptyRow(columnCount));

  // 12. Cost breakdown header
  const costBreakdownRow: any = { A: 'COST BREAKDOWN', B: '', C: '' };
  sortedSets.forEach((_, index) => {
    const cols = getSectionColumns(index + 1);
    costBreakdownRow[cols.label] = 'COST BREAKDOWN';
    costBreakdownRow[cols.amount] = '';
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      costBreakdownRow[gapCol] = '';
    }
  });
  data.push(costBreakdownRow);

  data.push(createEmptyRow(columnCount));

  // Cost categories
  const costCategories = [
    { key: 'bonus', label: 'Bonus' },
    { key: 'scheduling', label: 'Scheduling' },
    { key: 'referrals', label: 'Referrals' },
    { key: 'management', label: 'MGMT' },
    { key: 'marketing', label: 'Marketing' },
    { key: 'tools', label: 'Tools' },
    { key: 'commission', label: 'Commission' },
    { key: 'otherCosts', label: 'Other Costs' }
  ];

  costCategories.forEach(({ key, label }) => {
    const categoryRow: any = {
      A: label,
      B: formatCurrencyGBP(overallTotals[key as keyof PaySummaryTotals] as number),
      C: ''
    };
    sortedSets.forEach((set, index) => {
      const cols = getSectionColumns(index + 1);
      categoryRow[cols.label] = label;
      categoryRow[cols.amount] = formatCurrencyGBP(setTotals[set][key as keyof PaySummaryTotals] as number);
      if (index < sortedSets.length - 1) {
        const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
        categoryRow[gapCol] = '';
      }
    });
    data.push(categoryRow);

    // Add individual cost items for categories that have them
    if (['referrals', 'commission', 'otherCosts'].includes(key)) {
      const categoryMap = {
        referrals: 'Referral',
        commission: 'Commission',
        otherCosts: 'Other'
      };
      addCostDetailsRows(data, costs, categoryMap[key as keyof typeof categoryMap], sortedSets);
    }
  });

  // Calculate and add summary rows
  const internalCosts = overallTotals.bonus + overallTotals.scheduling + overallTotals.referrals +
                       overallTotals.management + overallTotals.marketing + overallTotals.tools +
                       overallTotals.commission + overallTotals.otherCosts;
  const totalCosts = internalCosts + overallTotals.totalSales;

  data.push(createEmptyRow(columnCount));

  // Internal costs row
  const internalCostsRow: any = { A: 'INTERNAL COSTS', B: formatCurrencyGBP(internalCosts), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    const setInternalCosts = setTotals[set].bonus + setTotals[set].scheduling + setTotals[set].referrals +
                            setTotals[set].management + setTotals[set].marketing + setTotals[set].tools +
                            setTotals[set].commission + setTotals[set].otherCosts;
    internalCostsRow[cols.label] = 'INTERNAL COSTS';
    internalCostsRow[cols.amount] = formatCurrencyGBP(setInternalCosts);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      internalCostsRow[gapCol] = '';
    }
  });
  data.push(internalCostsRow);

  data.push(createEmptyRow(columnCount));

  // Total costs row
  const totalCostsRow: any = { A: 'TOTAL COSTS', B: formatCurrencyGBP(totalCosts), C: '' };
  sortedSets.forEach((set, index) => {
    const cols = getSectionColumns(index + 1);
    const setInternalCosts = setTotals[set].bonus + setTotals[set].scheduling + setTotals[set].referrals +
                            setTotals[set].management + setTotals[set].marketing + setTotals[set].tools +
                            setTotals[set].commission + setTotals[set].otherCosts;
    const setTotalCosts = setInternalCosts + setTotals[set].totalSales;
    totalCostsRow[cols.label] = 'TOTAL COSTS';
    totalCostsRow[cols.amount] = formatCurrencyGBP(setTotalCosts);
    if (index < sortedSets.length - 1) {
      const gapCol = String.fromCharCode(cols.amount.charCodeAt(0) + 1);
      totalCostsRow[gapCol] = '';
    }
  });
  data.push(totalCostsRow);

  return { data, columnCount };
};